import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';

const DEFAULT_SEGMENT_DURATION_MINUTES = 30;
const MAX_SESSION_SEGMENTS = 5;

export const getSessionContentId = (slot: FlowSlotDto): string | undefined => {
  if (slot.contentType !== 'Session' || !slot.content) return undefined;
  const content = slot.content;
  if (typeof content === 'string') return content;
  return content.id;
};

export const getSessionSegmentSlots = (flow: FlowDto | undefined, sessionContentId: string): FlowSlotDto[] => {
  if (!flow?.slots?.length) return [];
  return flow.slots.filter((s) => s.contentType === 'Session' && getSessionContentId(s) === sessionContentId);
};

export const getSessionSegmentCount = (flow: FlowDto | undefined, slot: FlowSlotDto): number => {
  const sessionContentId = getSessionContentId(slot);
  if (!sessionContentId) return 0;
  return getSessionSegmentSlots(flow, sessionContentId).length;
};

/** 1-based index of this slot within its session segment group (0 when not found). */
export const getSessionSegmentIndex = (flow: FlowDto | undefined, slot: FlowSlotDto): number => {
  const sessionContentId = getSessionContentId(slot);
  if (!sessionContentId) return 0;
  const index = getSessionSegmentSlots(flow, sessionContentId).findIndex((s) => s.id === slot.id);
  return index >= 0 ? index + 1 : 0;
};

export const isSegmentedSession = (flow: FlowDto | undefined, slot: FlowSlotDto): boolean =>
  getSessionSegmentCount(flow, slot) > 1;

/** `1/2` for split session slots; null when the session is not split. */
export const formatSessionSegmentIndexLabel = (flow: FlowDto | undefined, slot: FlowSlotDto): string | null => {
  if (slot.contentType !== 'Session') {
    return null;
  }
  if (!isSegmentedSession(flow, slot)) {
    return null;
  }
  const index = getSessionSegmentIndex(flow, slot);
  const total = getSessionSegmentCount(flow, slot);
  if (index <= 0 || total <= 1) {
    return null;
  }
  return `${index}/${total}`;
};

export const getFirstSessionSegmentSlot = (
  flow: FlowDto | undefined,
  sessionContentId: string,
): FlowSlotDto | undefined => getSessionSegmentSlots(flow, sessionContentId)[0];

/** Flow slot id for contributor roleslots — segment anchor when the selected slot is in the group, else selected slot (e.g. PLAY-only overlay). */
export const resolveContributorAnchorFlowSlotId = (flow: FlowDto | undefined, selectedSlot: FlowSlotDto): string => {
  const sessionContentId = getSessionContentId(selectedSlot);
  if (!sessionContentId || !flow) return selectedSlot.id;
  const segments = getSessionSegmentSlots(flow, sessionContentId);
  if (segments.length === 0 || !segments.some((s) => s.id === selectedSlot.id)) {
    return selectedSlot.id;
  }
  return getFirstSessionSegmentSlot(flow, sessionContentId)?.id ?? selectedSlot.id;
};

export const isSessionSegmentAnchor = (flow: FlowDto | undefined, slot: FlowSlotDto): boolean => {
  const sessionContentId = getSessionContentId(slot);
  if (!sessionContentId) return false;
  const anchor = getFirstSessionSegmentSlot(flow, sessionContentId);
  return anchor?.id === slot.id;
};

export const getNextSessionSegmentAnchor = (
  flow: FlowDto | undefined,
  sessionContentId: string,
  excludingSlotId: string,
): FlowSlotDto | undefined => getSessionSegmentSlots(flow, sessionContentId).find((s) => s.id !== excludingSlotId);

export const computeNewSegmentDuration = (session: SessionDto | undefined, segmentSlots: FlowSlotDto[]): number => {
  const maxExisting = segmentSlots.reduce((max, s) => {
    const duration = s.plannedDurationMinutes ?? s.computedDurationMinutes ?? 0;
    return Math.max(max, duration);
  }, 0);
  const sessionPlanned = session?.plannedDurationMinutes ?? 0;
  if (sessionPlanned > maxExisting) return sessionPlanned;
  return DEFAULT_SEGMENT_DURATION_MINUTES;
};

export const canSplitSessionSlot = (flow: FlowDto | undefined, slot: FlowSlotDto): boolean =>
  slot.contentType === 'Session' && getSessionSegmentCount(flow, slot) < MAX_SESSION_SEGMENTS;

export const shouldOfferSlotRemoval = (slot: FlowSlotDto, flow: FlowDto | undefined): boolean => {
  if (isSegmentedSession(flow, slot)) return true;
  if (slot.contentType === 'Session' && slot.content?.isInLibrary) return true;
  return false;
};

/** Whether a FlowSlot role entity belongs to the segmented session group for the current anchor. */
export const matchesSessionSegmentFlowSlotEntity = (
  flow: FlowDto | undefined,
  selectedSlot: FlowSlotDto,
  anchorFlowSlotId: string,
  roleSlotEntityId: string | undefined,
): boolean => {
  if (!roleSlotEntityId) return false;
  if (roleSlotEntityId === anchorFlowSlotId) return true;

  const sessionContentId = getSessionContentId(selectedSlot);
  if (!sessionContentId) return false;

  const segmentSlotIds = getSessionSegmentSlots(flow, sessionContentId).map((s) => s.id);
  if (segmentSlotIds.includes(roleSlotEntityId)) return true;

  // Role slot still points at a removed segment after anchor deletion (until refetch/reassign).
  if (isSegmentedSession(flow, selectedSlot)) {
    const flowSlotIds = new Set(flow?.slots?.map((s) => s.id) ?? []);
    if (!flowSlotIds.has(roleSlotEntityId)) return true;
  }

  return false;
};
